import { Inject, Injectable } from '@angular/core';
import { LoggedOutPageReady, LoginFailed, LoginSuccess, LIBRARY_CONFIG } from '../../../shared';
import { BehaviorSubject, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthError, EventType, InteractionStatus } from '@azure/msal-browser';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
import * as i2 from "@azure/msal-angular";
import * as i3 from "./login.service";
import * as i4 from "./inactivity.service";
import * as i5 from "../../../shared";
export class MsalAuthFacadeService {
  store;
  msalService;
  msalBroadcastService;
  loginService;
  inactivityService;
  appActivityService;
  destroyed$;
  LOGIN_FAILED_RETRY_KEY = 'msalLoginRetry';
  isLoggedInSubject = new BehaviorSubject(false);
  isLoggedIn$ = this.isLoggedInSubject.asObservable();
  passwordResetAuth;
  constructor(store, msalService, msalBroadcastService, loginService, inactivityService, appActivityService, destroyed$, libConfig) {
    this.store = store;
    this.msalService = msalService;
    this.msalBroadcastService = msalBroadcastService;
    this.loginService = loginService;
    this.inactivityService = inactivityService;
    this.appActivityService = appActivityService;
    this.destroyed$ = destroyed$;
    this.passwordResetAuth = libConfig.passwordResetAuthority;
  }
  initializeMsal(noAuth, isAdmin) {
    this.msalService.handleRedirectObservable().pipe(takeUntilDestroyed(this.destroyed$)).subscribe(response => {
      const activeAccount = this.msalService.instance.getActiveAccount();
      if (activeAccount === null && noAuth) {
        this.store.dispatch(new LoggedOutPageReady());
        this.inactivityService.clearTimer();
      } else if (!isAdmin) {
        this.inactivityService.startTimer();
      }
      if (!response && noAuth && activeAccount) {
        this.loginService.login();
      } else if (!response && activeAccount) {
        this.msalService.acquireTokenSilent({
          scopes: ['openid', 'offline_access', 'email']
        });
      }
      if (response?.idToken) {
        this.appActivityService.sendAppActivity().pipe(takeUntilDestroyed(this.destroyed$)).subscribe({
          error: e => {
            console.error(e);
          }
        });
        const claims = response.idTokenClaims;
        this.store.dispatch(new LoginSuccess(response.idToken, claims.exp));
      }
    });
    this.msalBroadcastService.msalSubject$.pipe(filter(msg => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE), takeUntilDestroyed(this.destroyed$)).subscribe(result => {
      // AADB2C90118 => user clicked “Forgot Password?”
      if (result.error instanceof AuthError && result.error.message.includes('AADB2C90118')) {
        const redirectRequest = {
          authority: this.passwordResetAuth,
          scopes: ['openid']
        };
        this.loginService.login(redirectRequest);
      }
      if (result.eventType === EventType.LOGIN_FAILURE) {
        const retryCount = parseInt(localStorage.getItem(this.LOGIN_FAILED_RETRY_KEY) || '0');
        const errorCode = result.error.errorCode;
        if (errorCode === 'no_cached_authority_error' && retryCount < 1) {
          // In some cases this error can occur, which is fixed by a reload.
          // A timeout must be used for the reload to work.
          localStorage.setItem(this.LOGIN_FAILED_RETRY_KEY, (retryCount + 1).toString());
          setTimeout(() => window.location.reload(), 200);
        } else {
          this.store.dispatch(new LoginFailed(result?.error?.message ?? ''));
        }
      }
      const payload = result.payload;
      if (result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        localStorage.removeItem(this.LOGIN_FAILED_RETRY_KEY);
        this.msalService.instance.setActiveAccount(payload.account);
        const claims = payload.idTokenClaims;
        const userContext = claims.extension_user_context;
        if (!userContext) {
          this.loginService.logout();
        }
      }
      if (payload?.idToken) {
        localStorage.removeItem(this.LOGIN_FAILED_RETRY_KEY);
        const claims = payload.idTokenClaims;
        this.store.dispatch(new LoginSuccess(payload.idToken, claims.exp));
        this.loginService.setClaimValues(claims);
        this.updateIsLoggedInState();
      }
    });
    this.msalBroadcastService.inProgress$.pipe(filter(status => status === InteractionStatus.None), takeUntilDestroyed(this.destroyed$))
    // leaving this in to show there is a return value
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .subscribe(() => {
      this.checkAndSetActiveAccount();
    });
  }
  updateIsLoggedInState() {
    const accounts = this.msalService.instance.getAllAccounts();
    this.isLoggedInSubject.next(accounts.length > 0);
  }
  checkAndSetActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      this.msalService.instance.setActiveAccount(this.msalService.instance.getAllAccounts()[0]);
    }
  }
  static ɵfac = function MsalAuthFacadeService_Factory(t) {
    return new (t || MsalAuthFacadeService)(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.MsalService), i0.ɵɵinject(i2.MsalBroadcastService), i0.ɵɵinject(i3.LoginService), i0.ɵɵinject(i4.InactivityService), i0.ɵɵinject(i5.AppActivityApiService), i0.ɵɵinject(i0.DestroyRef), i0.ɵɵinject(LIBRARY_CONFIG));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: MsalAuthFacadeService,
    factory: MsalAuthFacadeService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MsalAuthFacadeService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.Store
  }, {
    type: i2.MsalService
  }, {
    type: i2.MsalBroadcastService
  }, {
    type: i3.LoginService
  }, {
    type: i4.InactivityService
  }, {
    type: i5.AppActivityApiService
  }, {
    type: i0.DestroyRef
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [LIBRARY_CONFIG]
    }]
  }], null);
})();
